import React, { useState, useEffect } from "react";
import SquareGrid from "./SquareGrid";
import SquarePlot from "./SquarePlot";
import SliderInput from "./SliderInput";
import ManualInput from "./ManualInput";
import IconButton from "@mui/material/IconButton";
import { Locked, Unlocked, Plot, Grid } from "./icons";
import themeOptions from "../themeOptions";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./SquareGrid.css";

function mathSquareSpiral(n: number): [number, number] {
  const lowerRoot = Math.floor(Math.sqrt(n));
  const anchor = lowerRoot ** 2;
  let location: [number, number];
  if (lowerRoot % 2 === 0) {
    location = [lowerRoot / -2, lowerRoot / 2];
    location[1] -= Math.min(n - anchor, lowerRoot);
    location[0] += Math.max(n - anchor - lowerRoot, 0);
  } else {
    location = [(lowerRoot - 1) / 2 + 1, (lowerRoot - 1) / -2];
    location[1] += Math.min(n - anchor, lowerRoot);
    location[0] -= Math.max(n - anchor - lowerRoot, 0);
  }
  return location;
}

const theme = createTheme(themeOptions);

interface InteractiveCardProps {
  inViewport?: boolean;
  forwardedRef?: React.Ref<HTMLDivElement>;
  type: "fss" | "lss" | string;
}

interface PlotPoint {
  n: number;
  x: number;
  y: number;
}

const InteractiveCard: React.FC<InteractiveCardProps> = ({
  inViewport = false,
  forwardedRef,
  type,
}) => {
  const matches = useMediaQuery("(min-width:500px)");
  const [squares, setSquares] = useState(25);
  const [squarray, setSquarray] = useState<number[][]>([
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
  ]);
  const [plotData, setPlotData] = useState<PlotPoint[]>([]);
  const [index, setIndex] = useState(0);
  const [locked, setLocked] = useState(true);
  const [plotMode, setPlotMode] = useState(0);
  const wh = Math.ceil(Math.sqrt(squares));
  const origin = Math.floor((wh - 1) / 2);
  const iconSize = matches ? "0.9em" : "0.8em";

  const spiralFunctions: Record<
    string,
    { fn: (origin: number) => void; delay: number; range: [number, number] }
  > = {
    fss: { fn: iterateFastSquareSpiral, delay: 2000, range: [4, 144] },
    lss: { fn: iterateLineSquareSpiral, delay: 7000, range: [4, 144] },
  };

  let drawDelay = Math.min(
    (spiralFunctions[type]?.delay ?? 2000) / squares,
    100
  );
  if (drawDelay < 10) {
    drawDelay = 0;
  }

  function newInput(input: number) {
    if (index !== -1) {
      return;
    }
    setSquares(input);
    const size = Math.ceil(Math.sqrt(input));
    const newArray = Array.from({ length: size }, () =>
      new Array(size).fill(0)
    );
    setSquarray(newArray);
    setPlotData([]);
    setIndex(0);
  }

  function toggleLock() {
    setLocked((prev) => !prev);
  }

  function togglePlot() {
    setPlotMode((prev) => (prev + 1) % 3);
  }

  function iterateFastSquareSpiral(originIndex: number) {
    const [ix, iy] = mathSquareSpiral(index);
    const [x, y] = [ix + originIndex, originIndex + iy];
    const tempSquarray = squarray.map((row) => [...row]);
    if (tempSquarray[y] && typeof tempSquarray[y][x] !== "undefined") {
      tempSquarray[y][x] = 1;
    }
    const tempPlotData = [...plotData, { n: index, x: ix, y: iy }];
    setPlotData(tempPlotData);
    setSquarray(tempSquarray);
    setIndex(index + 1);
  }

  function iterateLineSquareSpiral(originIndex: number) {
    let nextHighestSquare = Math.ceil(Math.sqrt(index + 1)) ** 2;
    if (nextHighestSquare - index > Math.sqrt(nextHighestSquare)) {
      nextHighestSquare = nextHighestSquare - Math.sqrt(nextHighestSquare);
    }
    if (nextHighestSquare + 1 > squares) {
      nextHighestSquare = squares - 1;
    }
    const tempSquarray = squarray.map((row) => [...row]);
    const tempPlotData = [...plotData];
    for (let i = index; i < nextHighestSquare + 1; i++) {
      const [ix, iy] = mathSquareSpiral(i);
      const [x, y] = [ix + originIndex, iy + originIndex];
      if (tempSquarray[y] && typeof tempSquarray[y][x] !== "undefined") {
        tempSquarray[y][x] = 1;
      }
      tempPlotData.push({ n: i, x: ix, y: iy });
    }
    setPlotData(tempPlotData);
    setSquarray(tempSquarray);
    setIndex(nextHighestSquare + 1);
  }

  useEffect(() => {
    if (inViewport && spiralFunctions[type]) {
      if (index < squares && index >= 0) {
        const timeout = setTimeout(() => {
          spiralFunctions[type].fn(origin);
        }, drawDelay);
        return () => clearTimeout(timeout);
      } else if (index >= squares) {
        setIndex(-1);
      }
    }
  }, [index, inViewport, type, squares, drawDelay, origin]);

  return (
    <div className={"DisplayCard"} ref={forwardedRef}>
      {plotMode ? (
        <div className="SquareGrid">
          <SquarePlot
            squarrayLength={squarray.length}
            plotData={plotData}
            index={index}
            origin={origin}
            linear={!!plotMode}
          />
        </div>
      ) : (
        <SquareGrid squares={squarray} index={index} />
      )}
      <div className="gridControl">
        <ThemeProvider theme={theme}>
          <div className="sliderBox">
            {locked ? (
              <SliderInput
                newInput={newInput}
                matches={matches}
              />
            ) : (
              <ManualInput
                newInput={newInput}
                matches={matches}
              />
            )}
          </div>
          <div className="gridButtons">
            <IconButton
              aria-label="lock"
              onClick={toggleLock}
              size="small"
              style={{ fontSize: iconSize }}
            >
              {locked ? <Locked ic={""} /> : <Unlocked ic={""} />}
            </IconButton>
            <IconButton
              aria-label="plot"
              onClick={togglePlot}
              size="small"
              style={{ fontSize: iconSize }}
            >
              {plotMode ? (
                <Plot linear={plotMode} />
              ) : (
                <Grid ic={""} />
              )}
            </IconButton>
          </div>
        </ThemeProvider>
      </div>
    </div>
  );
};

export default InteractiveCard;


